import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ShoppingCart,
    LogOut,
    User,
    Compass,
    ChevronDown,
    Users,
    Calendar,
    DollarSign,
    CreditCard,
    Menu,
    X,
    Settings,
} from 'lucide-react';
import AxonallLogo from '../AxonallLogo';
import travelAgentService from '../../services/travelAgentService';
import { useCart } from '../../contexts/CartContext';

const navLinkClass =
    'text-gray-900 hover:text-black text-[11px] tracking-[0.18em] uppercase font-poppinsRegular transition-colors duration-200';

const NavSeparator = () => (
    <span className="text-gray-300 select-none text-xs hidden lg:inline" aria-hidden="true">|</span>
);

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { cartItemCount, clearCartCount } = useCart();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen && !event.target.closest('.profile-dropdown-container')) {
                setIsDropdownOpen(false);
            }
        };

        window.addEventListener('mousedown', handleClickOutside);
        return () => window.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    useEffect(() => {
        setMobileMenuOpen(false);
        setIsDropdownOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setMobileMenuOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!travelAgentService.isAuthenticated()) return;

        const loadProfile = async () => {
            try {
                const response = await travelAgentService.getProfile();
                setProfile(response.data);
            } catch (error) {
                console.error('Failed to load profile in header:', error);
            }
        };

        loadProfile();
    }, [location.pathname]);

    const handleLogout = () => {
        clearCartCount();
        travelAgentService.logout();
        navigate('/login');
    };

    const menuItems = [
        { label: 'Reserve Membership', icon: <User className="w-4 h-4" />, path: '/profile' },
        { label: 'View Clients', icon: <Users className="w-4 h-4" />, path: '/clients' },
        { label: 'My Bookings', icon: <Calendar className="w-4 h-4" />, path: '/user-bookings' },
        { label: 'My Commissions', icon: <DollarSign className="w-4 h-4" />, path: '/user-commission' },
        { label: 'Payment Methods', icon: <CreditCard className="w-4 h-4" />, path: '/bank-account' },
        { label: 'White Labeling', icon: <Settings className="w-4 h-4" />, path: '/white-labeling' },
        { label: 'Contact Us', icon: <Compass className="w-4 h-4" />, path: '/contact' },
    ];

    const primaryNavItems = [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Experiences', path: '/experiences' },
        { label: 'Hotels', path: '/hotels' },
        { label: 'Off-Market', path: '/off-market-stays' },
        { label: 'Private Jets', path: '/private-jets' },
        { label: 'Cart', path: '/cart', badge: cartItemCount },
        { label: 'AI Concierge', path: '/ai-concierge' },
    ];

    const go = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    const isActive = (path) => {
        if (path === '/dashboard') return location.pathname === '/dashboard';
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const activeNavClass = (path) =>
        isActive(path) ? `${navLinkClass} text-black` : navLinkClass;

    return (
        <header
            className="sticky top-0 z-[60] bg-white"
            style={{ boxShadow: '0 1px 0 rgba(0,0,0,0.06)' }}
        >
            {/* Row 1 — centered logo */}
            <div className="flex justify-center items-center py-4 border-b border-gray-100">
                <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="focus:outline-none"
                    aria-label="Go to dashboard"
                >
                    <AxonallLogo size="text-2xl" color="text-gray-900" />
                </button>
            </div>

            {/* Row 2 — desktop nav */}
            <nav
                className="hidden lg:flex justify-center items-center gap-5 xl:gap-8 py-3 px-4"
                aria-label="Primary"
            >
                {primaryNavItems.map((item, index) => (
                    <React.Fragment key={item.path}>
                        {index > 0 && <NavSeparator />}
                        <button
                            type="button"
                            onClick={() => navigate(item.path)}
                            className={`${activeNavClass(item.path)} relative inline-flex items-center gap-1.5`}
                        >
                            {item.label}
                            {item.badge > 0 && (
                                <span className="min-w-[1rem] h-4 px-1 flex items-center justify-center bg-gray-900 text-white text-[9px] font-poppinsRegular tracking-normal">
                                    {item.badge > 99 ? '99+' : item.badge}
                                </span>
                            )}
                        </button>
                    </React.Fragment>
                ))}

                <NavSeparator />

                <div className="profile-dropdown-container relative" ref={dropdownRef}>
                    <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={`${navLinkClass} inline-flex items-center gap-1.5`}
                        aria-expanded={isDropdownOpen}
                    >
                        Account
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isDropdownOpen && (
                        <div className="absolute right-0 mt-3 w-56 bg-white border border-gray-200/60 shadow-sm py-2 z-[65]">
                            <div className="px-4 py-3 border-b border-gray-100 mb-1">
                                <p className="text-[10px] font-poppinsLight text-gray-400 uppercase tracking-[0.2em]">Logged in as</p>
                                <p className="text-sm font-poppinsRegular text-gray-900 truncate mt-1">{profile?.name || 'Travel Advisor'}</p>
                                <p className="text-xs font-poppinsLight text-gray-500 truncate">{profile?.email}</p>
                            </div>

                            {menuItems.map((item, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => {
                                        navigate(item.path);
                                        setIsDropdownOpen(false);
                                    }}
                                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors font-poppinsRegular"
                                >
                                    <span className="text-gray-400">{item.icon}</span>
                                    <span>{item.label}</span>
                                </button>
                            ))}

                            <div className="h-px bg-gray-100 my-1" />

                            <button
                                type="button"
                                onClick={handleLogout}
                                className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-poppinsRegular"
                            >
                                <LogOut className="w-4 h-4" />
                                <span>Logout</span>
                            </button>
                        </div>
                    )}
                </div>
            </nav>

            {/* Row 2 — mobile bar */}
            <div className="flex lg:hidden justify-between items-center py-3 px-4 border-t border-gray-100">
                <button
                    type="button"
                    onClick={() => setMobileMenuOpen(true)}
                    className={navLinkClass}
                    aria-expanded={mobileMenuOpen}
                    aria-label="Open menu"
                >
                    <Menu className="w-5 h-5" />
                </button>

                <span className="text-[10px] tracking-[0.35em] uppercase font-poppinsLight text-gray-400">
                    Studio
                </span>

                <button
                    type="button"
                    onClick={() => navigate('/cart')}
                    className={`${navLinkClass} relative inline-flex items-center gap-1`}
                    aria-label="Cart"
                >
                    <ShoppingCart className="w-5 h-5" />
                    {cartItemCount > 0 && (
                        <span className="absolute -top-1 -right-2 min-w-[1rem] h-4 px-1 flex items-center justify-center bg-gray-900 text-white text-[9px] font-poppinsRegular">
                            {cartItemCount > 99 ? '99+' : cartItemCount}
                        </span>
                    )}
                </button>
            </div>

            {mobileMenuOpen && (
                <>
                    <button
                        type="button"
                        className="fixed inset-0 z-[70] bg-black/40 lg:hidden"
                        aria-label="Close menu"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div
                        className="fixed top-0 right-0 bottom-0 z-[80] w-full max-w-sm bg-white border-l border-gray-200 shadow-xl lg:hidden flex flex-col overflow-hidden"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Navigation menu"
                    >
                        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 shrink-0">
                            <AxonallLogo size="text-lg" color="text-gray-900" />
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(false)}
                                className="text-gray-500 hover:text-gray-900 transition-colors"
                                aria-label="Close menu"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <nav className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-1" aria-label="Mobile primary">
                            {primaryNavItems.map(({ label, path, badge }) => (
                                <button
                                    key={path}
                                    type="button"
                                    onClick={() => go(path)}
                                    className={`w-full flex items-center justify-between px-3 py-3 text-left ${activeNavClass(path)}`}
                                >
                                    <span>{label}</span>
                                    {badge > 0 && (
                                        <span className="min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center bg-gray-900 text-white text-[10px] font-poppinsRegular tracking-normal">
                                            {badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>

                        <div className="border-t border-gray-100 px-4 py-4 space-y-1 shrink-0 max-h-[45vh] overflow-y-auto">
                            <p className="px-3 pt-1 pb-2 text-[10px] tracking-[0.35em] uppercase font-poppinsLight text-gray-400">
                                Account
                            </p>
                            {profile?.name && (
                                <p className="px-3 pb-2 text-sm font-poppinsRegular text-gray-900 truncate">{profile.name}</p>
                            )}
                            {menuItems.map((item, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => go(item.path)}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors text-[11px] tracking-[0.12em] uppercase font-poppinsRegular"
                                >
                                    <span className="text-gray-400">{item.icon}</span>
                                    <span>{item.label}</span>
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => {
                                    setMobileMenuOpen(false);
                                    handleLogout();
                                }}
                                className="w-full flex items-center gap-3 px-3 py-3 mt-2 text-left text-red-600 hover:bg-red-50 transition-colors text-[11px] tracking-[0.12em] uppercase font-poppinsRegular border border-red-100"
                            >
                                <LogOut className="w-4 h-4 shrink-0" />
                                Logout
                            </button>
                        </div>
                    </div>
                </>
            )}
        </header>
    );
};

export default Header;
